import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash, Tag, Pencil, Paperclip, MagnifyingGlass, CurrencyInr, CheckCircle, Check, X } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { usePagination } from '../components/TablePagination';
import { Amount } from '../contexts/AmountVisibilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Privacy-aware money formatter (uses no decimals to match expense rounding).
const fmtINRRaw = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const M = ({ v, className }) => <Amount value={v} format={fmtINRRaw} className={className} />;

const empty = () => ({
  id: '',
  category_id: '',
  amount: 0,
  date: new Date().toISOString().split('T')[0],
  description: '',
  company_id: '',
  vendor_name: '',
  vendor_gstin: '',
  taxable_amount: 0,
  cgst: 0,
  sgst: 0,
  igst: 0,
  is_gst_eligible_itc: false,
  receipt_url: '',
});

export const ExpensesPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [edit, setEdit] = useState(false);

  // Categories modal state
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingCatId, setEditingCatId] = useState('');
  const [editingCatName, setEditingCatName] = useState('');

  const [form, setForm] = useState(empty());
  const [search, setSearch] = useState('');
  // Filters
  const [companyFilter, setCompanyFilter] = useState('all');           // all | <company_id> | __unassigned
  const [statusFilter, setStatusFilter] = useState('all');             // all | paid | pending
  const [catFilter, setCatFilter] = useState('all');                   // all | <category_id>
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [payDialog, setPayDialog] = useState(null);
  const [payForm, setPayForm] = useState({ paid_via: 'bank', paid_account: '', paid_account_id: '', paid_reference: '', paid_date: new Date().toISOString().slice(0, 10), paid_notes: '' });
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [expRes, catRes, coRes] = await Promise.all([
        axios.get(`${API_URL}/api/expenses`, { withCredentials: true }),
        axios.get(`${API_URL}/api/expense-categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/companies`, { withCredentials: true }),
      ]);
      setExpenses([...(expRes.data || [])]);
      setCategories([...(catRes.data || [])]);
      setCompanies([...(coRes.data || [])]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };


  // Category CRUD
  async function doAddCategory() {
    const name = newCatName.trim();
    if (!name) return;
    try {
      await axios.post(`${API_URL}/api/expense-categories`, { name }, { withCredentials: true });
      setNewCatName('');
      toast.success('Category added');
      await loadData();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    }
  }

  const startEditCat = (cat) => { setEditingCatId(cat.id); setEditingCatName(cat.name); };
  const cancelEditCat = () => { setEditingCatId(''); setEditingCatName(''); };

  const saveEditCat = async () => {
    const name = editingCatName.trim();
    if (!name) return;
    try {
      await axios.put(`${API_URL}/api/expense-categories/${editingCatId}`, { name }, { withCredentials: true });
      toast.success('Category renamed');
      cancelEditCat();
      await loadData();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Existing expenses keep their assignment.')) return;
    try {
      await axios.delete(`${API_URL}/api/expense-categories/${id}`, { withCredentials: true });
      toast.success('Category deleted');
      await loadData();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ─── Expense CRUD ────────────────────────────────────────────────
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const { id: formId, ...rest } = form;
      const payload = {
        ...rest,
        amount: parseFloat(form.amount) || 0,
        taxable_amount: parseFloat(form.taxable_amount) || 0,
        cgst: parseFloat(form.cgst) || 0,
        sgst: parseFloat(form.sgst) || 0,
        igst: parseFloat(form.igst) || 0,
      };
      let expId = formId;
      if (edit) {
        await axios.put(`${API_URL}/api/expenses/${formId}`, { ...payload, id: formId }, { withCredentials: true });
      } else {
        const r = await axios.post(`${API_URL}/api/expenses`, payload, { withCredentials: true });
        expId = r.data?.id;
      }
      if (receiptFile && expId) {
        const fd = new FormData();
        fd.append('file', receiptFile);
        await axios.post(`${API_URL}/api/expenses/${expId}/receipt`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setExpenseOpen(false);
      setEdit(false);
      setForm(empty());
      setReceiptFile(null);
      loadData();
    } catch (error) {
      alert('Save failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const onEdit = (e) => {
    setForm({ ...empty(), ...e });
    setEdit(true);
    setReceiptFile(null);
    setExpenseOpen(true);
  };

  const onAdd = () => {
    setForm(empty());
    setEdit(false);
    setReceiptFile(null);
    setExpenseOpen(true);
  };

  const handleDeleteExpense = async (id) => {
    if (!id) {
      if (!window.confirm('This expense record is corrupted (missing ID). Delete & cleanup all such orphan records?')) return;
      try {
        const r = await axios.post(`${API_URL}/api/expenses/_maintenance/fix-empty-ids`, {}, { withCredentials: true });
        const gone = r.data.garbage_deleted || 0;
        const minted = r.data.fixed || 0;
        if (gone > 0 && minted === 0) {
          alert(`Deleted ${gone} empty/orphan record(s).`);
        } else if (gone > 0 && minted > 0) {
          alert(`Deleted ${gone} empty record(s), and gave ${minted} other(s) a fresh ID. You can now delete those normally.`);
        } else if (minted > 0) {
          alert(`Gave ${minted} record(s) a fresh ID. Click Delete on them again now.`);
        } else {
          alert('No orphan records found.');
        }
        await loadData();
      } catch (e) {
        alert('Cleanup failed: ' + (e.response?.data?.detail || e.message));
      }
      return;
    }
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API_URL}/api/expenses/${id}`, { withCredentials: true });
      await loadData();
    } catch (error) {
      alert('Delete failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // ─── Reimbursement payment ───────────────────────────────────────
  const openPayDialog = (exp) => {
    setPayDialog(exp);
    setPayForm({
      paid_via: 'bank',
      paid_account: '',
      paid_account_id: '',
      paid_reference: '',
      paid_date: new Date().toISOString().slice(0, 10),
      paid_notes: '',
    });
    if (bankAccounts.length === 0) {
      axios.get(`${API_URL}/api/bank-accounts`, { withCredentials: true })
        .then((r) => setBankAccounts(r.data || []))
        .catch(() => setBankAccounts([]));
    }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!payDialog) return;
    try {
      await axios.post(`${API_URL}/api/expenses/${payDialog.id}/payment`, payForm, { withCredentials: true });
      toast.success(`Payment recorded for ₹${Number(payDialog.amount).toLocaleString()}`);
      setPayDialog(null);
      await loadData();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const unmarkPaid = async (id) => {
    if (!window.confirm('Reverse payment record? The expense will go back to pending.')) return;
    try {
      await axios.post(`${API_URL}/api/expenses/${id}/unmark-paid`, {}, { withCredentials: true });
      toast.success('Payment record reversed');
      await loadData();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ─── Filter + sort + paginate ────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = expenses.filter((e) => {
      // Date range
      const d = String(e.date || '').slice(0, 10);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      // Company filter
      if (companyFilter !== 'all') {
        const co = e.company_id || '';
        if (companyFilter === '__unassigned' && co) return false;
        if (companyFilter !== '__unassigned' && co !== companyFilter) return false;
      }
      // Status filter
      if (statusFilter === 'paid' && e.payment_status !== 'paid') return false;
      if (statusFilter === 'pending' && e.payment_status === 'paid') return false;
      // Category filter
      if (catFilter !== 'all' && e.category_id !== catFilter) return false;
      // Search
      if (!q) return true;
      const cat = categories.find((c) => c.id === e.category_id)?.name || '';
      return [e.description, e.vendor_name, e.vendor_gstin, cat, e.date, e.employee_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    // Newest first
    list.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    return list;
  }, [expenses, search, categories, companyFilter, statusFilter, catFilter, dateFrom, dateTo]);

  // Company-wise totals on the CURRENTLY-FILTERED set (date/status/category/search respected,
  // but NOT the company-filter — otherwise the chips would always show the active one only).
  const companyTotals = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = expenses.filter((e) => {
      const d = String(e.date || '').slice(0, 10);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      if (statusFilter === 'paid' && e.payment_status !== 'paid') return false;
      if (statusFilter === 'pending' && e.payment_status === 'paid') return false;
      if (catFilter !== 'all' && e.category_id !== catFilter) return false;
      if (!q) return true;
      const cat = categories.find((c) => c.id === e.category_id)?.name || '';
      return [e.description, e.vendor_name, e.vendor_gstin, cat, e.date, e.employee_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
    const buckets = {};
    let unassigned = 0, paidSum = 0, pendingSum = 0;
    base.forEach((e) => {
      const amt = Number(e.amount) || 0;
      const co = e.company_id || '';
      if (co) buckets[co] = (buckets[co] || 0) + amt;
      else unassigned += amt;
      if (e.payment_status === 'paid') paidSum += amt; else pendingSum += amt;
    });
    const rows = companies.map((c) => ({ id: c.id, name: c.name, short: c.short, accent: c.accent || '#1D9E75', total: buckets[c.id] || 0 }));
    if (unassigned > 0) rows.push({ id: '__unassigned', name: 'Unassigned', short: '—', accent: '#6B7280', total: unassigned });
    rows.sort((a, b) => b.total - a.total);
    const grand = rows.reduce((s, r) => s + r.total, 0);
    return { rows, grand, count: base.length, paidSum, pendingSum };
  }, [expenses, search, categories, companies, statusFilter, catFilter, dateFrom, dateTo]);

  const { paged, controls, startIdx } = usePagination(filtered, { storageKey: 'expenses-page-size', testIdPrefix: 'expense' });

  return (
    <div data-testid="expenses-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {isAdmin ? 'Expenses Manager' : 'My Expenses'}
          </h1>
          <p className="text-[#52525B] text-sm">
            {isAdmin
              ? 'Track expenses, capture GST input credit, attach receipts, record reimbursements'
              : 'Submit expenses for reimbursement. Admin will record the payment once approved.'}
          </p>
        </div>
        <div className="flex gap-3">
          {isAdmin && (
            <Button variant="outline" onClick={() => setCatManagerOpen(true)} data-testid="manage-categories-btn">
              <Tag size={18} className="mr-2" /> Manage Categories
            </Button>
          )}
          <Button onClick={onAdd} data-testid="add-expense-btn" className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white rounded-sm">
            <Plus size={20} className="mr-2" />{isAdmin ? 'Add Expense' : 'Claim Expense'}
          </Button>
        </div>
      </div>

      {/* Summary tiles — company-wise totals + grand total */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-5">
        <Card
          className="p-3 cursor-pointer transition border-2"
          style={{ borderColor: companyFilter === 'all' ? '#1D9E75' : '#E4E4E7', background: companyFilter === 'all' ? '#F0FDF4' : 'white' }}
          onClick={() => setCompanyFilter('all')}
          data-testid="expense-tile-all"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">All Companies</div>
          <div className="text-2xl font-black mt-1 tabular-nums" data-testid="expense-total-all"><M v={companyTotals.grand} /></div>
          <div className="text-[10px] text-gray-400 mt-0.5">{companyTotals.count} expenses · <span className="text-emerald-700"><M v={companyTotals.paidSum} /> paid</span> · <span className="text-amber-700"><M v={companyTotals.pendingSum} /> pending</span></div>
        </Card>
        {companyTotals.rows.map((r) => (
          <Card
            key={r.id}
            className="p-3 cursor-pointer transition border-2"
            style={{ borderColor: companyFilter === r.id ? r.accent : '#E4E4E7', background: companyFilter === r.id ? (r.accent + '14') : 'white' }}
            onClick={() => setCompanyFilter(r.id)}
            data-testid={`expense-tile-${r.id}`}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider flex items-center justify-between">
              <span style={{ color: r.accent }}>{r.short}</span>
              <span className="text-gray-400 truncate ml-1 max-w-[110px]">{r.name}</span>
            </div>
            <div className="text-2xl font-black mt-1 tabular-nums" style={{ color: r.accent }}><M v={r.total} /></div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              {companyTotals.grand > 0 ? `${Math.round((r.total / companyTotals.grand) * 100)}% of total` : '0% of total'}
            </div>
          </Card>
        ))}
      </div>

      {/* Filters row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 p-3 rounded-md border border-[#E4E4E7] bg-white">
        <div className="md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by vendor, category, GSTIN, description, employee…"
            className="pl-9 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="expense-search"
          />
        </div>
        <div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9" data-testid="expense-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="paid">✅ Paid only</SelectItem>
              <SelectItem value="pending">⏳ Pending only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="h-9" data-testid="expense-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-1">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-xs" data-testid="expense-date-from" title="From" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-xs" data-testid="expense-date-to" title="To" />
        </div>
        {(search || companyFilter !== 'all' || statusFilter !== 'all' || catFilter !== 'all' || dateFrom || dateTo) && (
          <div className="md:col-span-5 flex justify-end">
            <button
              type="button"
              className="text-[11px] text-gray-500 hover:text-rose-600 underline"
              onClick={() => { setSearch(''); setCompanyFilter('all'); setStatusFilter('all'); setCatFilter('all'); setDateFrom(''); setDateTo(''); }}
              data-testid="expense-clear-filters"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      <Card className="border border-[#E4E4E7]">
        <div className="px-6 pt-5 pb-2">
          <h3 className="text-lg font-semibold">Expenses ({filtered.length})</h3>
        </div>
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F4F5]">
                <TableHead className="text-xs uppercase tracking-wider font-semibold">#</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Date</TableHead>
                {isAdmin && <TableHead className="text-xs uppercase tracking-wider font-semibold">Employee</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Category</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Vendor</TableHead>
                {isAdmin && <TableHead className="text-xs uppercase tracking-wider font-semibold">GST</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Description</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Receipt</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Payment</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.length === 0 ? (
                <TableRow><TableCell colSpan={isAdmin ? 11 : 8} className="text-center py-8 text-[#52525B]">No expenses found.</TableCell></TableRow>
              ) : paged.map((exp, idx) => {
                const cat = categories.find((c) => c.id === exp.category_id);
                const totalTax = (exp.cgst || 0) + (exp.sgst || 0) + (exp.igst || 0);
                const isPaid = exp.payment_status === 'paid';
                const PAID_VIA_LABEL = { bank: '🏦 Bank', upi: 'UPI', gpay: 'GPay', phonepe: 'PhonePe', paytm: 'Paytm', cash: '💵 Cash', cheque: '📝 Cheque', other: 'Other' };
                return (
                  <TableRow key={exp.id} data-testid={`expense-row-${exp.id}`}>
                    <TableCell className="text-gray-400">{startIdx + idx + 1}</TableCell>
                    <TableCell>{(() => { const [y, m, d] = String(exp.date || '').slice(0, 10).split('-'); return y ? `${d}/${m}/${y}` : exp.date; })()}</TableCell>
                    {isAdmin && <TableCell className="text-xs">{exp.employee_name || '—'}</TableCell>}
                    <TableCell><span className="px-2 py-1 bg-[#F4F4F5] text-xs rounded-sm">{cat?.name || 'N/A'}</span></TableCell>
                    <TableCell>{exp.vendor_name || '-'}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        {exp.is_gst_eligible_itc ? (
                          <span className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-sm font-medium" title={`ITC ${totalTax.toFixed(2)}`}>
                            ITC <Amount value={totalTax} format={(n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} />
                          </span>
                        ) : '-'}
                      </TableCell>
                    )}
                    <TableCell className="text-[#52525B] text-xs">{exp.description || '-'}</TableCell>
                    <TableCell>
                      {exp.receipt_url ? (
                        <a href={`${API_URL}${exp.receipt_url}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline text-xs flex items-center gap-1">
                          <Paperclip size={12} />View
                        </a>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </TableCell>
                    <TableCell className="font-semibold"><M v={exp.amount} /></TableCell>
                    <TableCell>
                      {isPaid ? (
                        <div className="flex flex-col gap-0.5" data-testid={`expense-paid-${exp.id}`}>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                            <CheckCircle size={10} weight="fill" /> PAID
                          </span>
                          <span className="text-[10px] text-gray-600">{PAID_VIA_LABEL[exp.paid_via] || exp.paid_via}</span>
                          {exp.paid_date && <span className="text-[10px] text-gray-400">{exp.paid_date}</span>}
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid={`expense-pending-${exp.id}`}>PENDING</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {isAdmin && !isPaid && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openPayDialog(exp)} data-testid={`record-payment-${exp.id}`}>
                            <CurrencyInr size={14} className="mr-1" /> Record Payment
                          </Button>
                        )}
                        {isAdmin && isPaid && (
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => unmarkPaid(exp.id)} data-testid={`unmark-paid-${exp.id}`}>
                            Unmark Paid
                          </Button>
                        )}
                        {(!isPaid || isAdmin) && (
                          <Button size="sm" variant="outline" onClick={() => onEdit(exp)} data-testid={`edit-expense-${exp.id}`}>
                            <Pencil size={14} />
                          </Button>
                        )}
                        {(!isPaid || isAdmin) && (
                          <Button size="sm" variant="outline" className="text-[#DC2626] hover:text-[#DC2626]" onClick={() => handleDeleteExpense(exp.id)} data-testid={`delete-expense-${exp.id}`}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {controls}
      </Card>

      {/* Manage Categories Modal */}
      <Dialog open={catManagerOpen} onOpenChange={setCatManagerOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="categories-modal">
          <DialogHeader>
            <DialogTitle>Expense Categories</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); doAddCategory(); }}
            className="flex gap-2 mb-4"
          >
            <Input
              placeholder="New category name (e.g. Office Supplies)"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              data-testid="new-category-input"
            />
            <Button type="submit" className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white shrink-0" data-testid="add-category-btn">
              <Plus size={16} className="mr-1" /> Add
            </Button>
          </form>
          <div className="space-y-2">
            {categories.length === 0 ? (
              <p className="text-sm text-[#52525B] text-center py-6">No categories yet. Add one above.</p>
            ) : categories.map((cat) => (
              <div key={cat.id} className="flex items-center justify-between gap-2 p-2 border border-[#E4E4E7] rounded-sm" data-testid={`category-item-${cat.id}`}>
                {editingCatId === cat.id ? (
                  <>
                    <Input
                      value={editingCatName}
                      onChange={(e) => setEditingCatName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEditCat(); if (e.key === 'Escape') cancelEditCat(); }}
                      className="h-8"
                      autoFocus
                      data-testid={`edit-category-input-${cat.id}`}
                    />
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={saveEditCat} data-testid={`save-category-btn-${cat.id}`}>
                      <Check size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-500" onClick={cancelEditCat}>
                      <X size={16} />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-medium flex-1 truncate">{cat.name}</span>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-600" onClick={() => startEditCat(cat)} data-testid={`edit-category-btn-${cat.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#DC2626]" onClick={() => handleDeleteCategory(cat.id)} data-testid={`delete-category-btn-${cat.id}`}>
                      <Trash size={14} />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? 'Edit Expense' : 'Add Expense'}</DialogTitle></DialogHeader>
          <form onSubmit={handleExpenseSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="expense-date-input" />
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder={categories.length ? "Select category" : "No categories — add one first"} /></SelectTrigger>
                  <SelectContent>
                    {categories.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-gray-500 text-center">
                        No categories yet.<br /><span className="text-emerald-600">Close this and click &ldquo;Manage Categories&rdquo; first.</span>
                      </div>
                    ) : categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Company (for P&L)</Label>
                <Select value={form.company_id || '__none'} onValueChange={(v) => setForm({ ...form, company_id: v === '__none' ? '' : v })}>
                  <SelectTrigger data-testid="expense-company"><SelectValue placeholder="Not assigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Not assigned —</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Total Amount (incl. tax) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="expense-amount-input" />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description" data-testid="expense-description-input" />
            </div>

            <div className="border rounded-md p-3 space-y-3 bg-slate-50/50">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Vendor Name</Label>
                  <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="expense-vendor-name" />
                </div>
                <div>
                  <Label className="text-xs">Vendor GSTIN</Label>
                  <Input value={form.vendor_gstin} onChange={(e) => setForm({ ...form, vendor_gstin: e.target.value.toUpperCase() })} data-testid="expense-vendor-gstin" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_gst_eligible_itc}
                  onChange={(e) => setForm({ ...form, is_gst_eligible_itc: e.target.checked })}
                  data-testid="expense-itc-checkbox"
                />
                Eligible for Input Tax Credit (will appear in GSTR-3B)
              </label>
              {form.is_gst_eligible_itc && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-1">
                  <div>
                    <Label className="text-xs">Taxable Value</Label>
                    <Input type="number" step="0.01" value={form.taxable_amount} onChange={(e) => setForm({ ...form, taxable_amount: e.target.value })} data-testid="expense-taxable" />
                  </div>
                  <div>
                    <Label className="text-xs">CGST</Label>
                    <Input type="number" step="0.01" value={form.cgst} onChange={(e) => setForm({ ...form, cgst: e.target.value })} data-testid="expense-cgst" />
                  </div>
                  <div>
                    <Label className="text-xs">SGST</Label>
                    <Input type="number" step="0.01" value={form.sgst} onChange={(e) => setForm({ ...form, sgst: e.target.value })} data-testid="expense-sgst" />
                  </div>
                  <div>
                    <Label className="text-xs">IGST</Label>
                    <Input type="number" step="0.01" value={form.igst} onChange={(e) => setForm({ ...form, igst: e.target.value })} data-testid="expense-igst" />
                  </div>
                </div>
              )}
            </div>

            <div className="border rounded-md p-3">
              <Label className="text-sm font-medium flex items-center gap-2"><Paperclip size={14} />Receipt (PDF / JPG / PNG, max 8 MB)</Label>
              <div className="mt-2 flex items-center gap-3">
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  className="text-xs"
                  data-testid="expense-receipt-input"
                />
                {form.receipt_url && !receiptFile && (
                  <a href={`${API_URL}${form.receipt_url}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline">View current</a>
                )}
                {receiptFile && <span className="text-xs text-gray-600">📎 {receiptFile.name}</span>}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setExpenseOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1D9E75] hover:bg-[#0F6E56]" data-testid="save-expense-btn">{edit ? 'Update' : 'Save'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin payment-recording dialog */}
      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Reimbursement</DialogTitle>
          </DialogHeader>
          {payDialog && (
            <form onSubmit={submitPayment} className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Employee</span>
                  <strong>{payDialog.employee_name || '—'}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Description</span>
                  <strong className="truncate ml-2">{payDialog.description || '—'}</strong>
                </div>
                <div className="flex justify-between mt-1 pt-1 border-t border-emerald-200">
                  <span className="text-gray-600">Amount to pay</span>
                  <strong className="text-emerald-700">₹{Number(payDialog.amount).toLocaleString()}</strong>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Paid Via *</Label>
                  <Select value={payForm.paid_via} onValueChange={(v) => setPayForm({ ...payForm, paid_via: v })}>
                    <SelectTrigger data-testid="payment-via-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">🏦 Bank Transfer (NEFT/IMPS/RTGS)</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="gpay">Google Pay</SelectItem>
                      <SelectItem value="phonepe">PhonePe</SelectItem>
                      <SelectItem value="paytm">Paytm</SelectItem>
                      <SelectItem value="cash">💵 Cash</SelectItem>
                      <SelectItem value="cheque">📝 Cheque</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Paid Date</Label>
                  <Input type="date" value={payForm.paid_date} onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })} data-testid="payment-date-input" />
                </div>
              </div>
              <div>
                <Label>From Account *</Label>
                <Select value={payForm.paid_account_id || '__free'} onValueChange={(v) => setPayForm({ ...payForm, paid_account_id: v === '__free' ? '' : v })}>
                  <SelectTrigger data-testid="payment-account-select"><SelectValue placeholder="Pick an account…" /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.filter((a) => a.is_active).map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.nickname} — ₹{Number(a.balance || 0).toLocaleString('en-IN')}
                      </SelectItem>
                    ))}
                    <SelectItem value="__free">— Not linked (free-text only) —</SelectItem>
                  </SelectContent>
                </Select>
                {!payForm.paid_account_id && (
                  <Input
                    className="mt-2"
                    value={payForm.paid_account}
                    onChange={(e) => setPayForm({ ...payForm, paid_account: e.target.value })}
                    placeholder='Free-text label (e.g. "HDFC ****1234")'
                    data-testid="payment-account-input"
                  />
                )}
                <p className="text-[10px] text-gray-500 mt-1">
                  {payForm.paid_account_id
                    ? '✓ This payment will be debited from the selected account in your Cash Book.'
                    : 'Linking an account auto-updates the live balance in Cash Book.'}
                </p>
              </div>
              <div>
                <Label>Reference / UTR (optional)</Label>
                <Input
                  value={payForm.paid_reference}
                  onChange={(e) => setPayForm({ ...payForm, paid_reference: e.target.value })}
                  placeholder="UTR number / cheque no. / UPI transaction id"
                  data-testid="payment-reference-input"
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input value={payForm.paid_notes} onChange={(e) => setPayForm({ ...payForm, paid_notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setPayDialog(null)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payment-submit-btn">Confirm Payment</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
